/* ═══════════════════════════════════════════════════════════════════════
   NoteFlow — auth-helpers.ts
   Cross-cutting auth helpers, constants, and shared state.
   ═══════════════════════════════════════════════════════════════════════ */

import { state } from './state';
import { FIREBASE_ENABLED, getFirebaseUid, getUserKey } from './config';
import { esc, el } from './utils';

declare const firebase: any;

// ─── Callback registration (avoids circular dependency with dashboard.ts) ───
let _showDashboardFn: (() => void) | null = null;

export function setShowDashboardFn(fn: () => void): void { _showDashboardFn = fn; }
export function getShowDashboardFn(): (() => void) | null { return _showDashboardFn; }

// ─── Constants ──────────────────────────────────────────────────────────────

export const SIGNUPS_DISABLED: boolean = import.meta.env.VITE_SIGNUPS_DISABLED !== 'false';
export const AUTH_USERS_KEY: string = 'fsgen-users';
export const AUTH_SESSION_KEY: string = 'fsgen-session';
export const SESSION_EXPIRY_MS: number = 8 * 60 * 60 * 1000;

// ─── Shared 2FA / reCAPTCHA state ───────────────────────────────────────────

export const mfaState: {
  recaptchaVerifier: any;
  mfaVerificationId: string | null;
  mfaResolver: any;
} = {
  recaptchaVerifier: null,
  mfaVerificationId: null,
  mfaResolver: null,
};

// ─── Shared UI helpers ──────────────────────────────────────────────────────

export function showAuthView(view: string): void {
  const authSignin = el('auth-signin');
  if (authSignin) authSignin.style.display = view === 'signin' ? 'block' : 'none';
  const authSignup = el('auth-signup');
  if (authSignup) authSignup.style.display = view === 'signup' ? 'block' : 'none';
  const authForgot = el('auth-forgot');
  if (authForgot) authForgot.style.display = view === 'forgot' ? 'block' : 'none';
  const auth2fa = el('auth-2fa');
  if (auth2fa) auth2fa.style.display = view === '2fa' ? 'block' : 'none';
  const signinMsg = el('signin-msg');
  if (signinMsg) signinMsg.innerHTML = '';
  const signupMsg = el('signup-msg');
  if (signupMsg) signupMsg.innerHTML = '';
  const forgotMsg = el('forgot-msg');
  if (forgotMsg) forgotMsg.innerHTML = '';
  const twoFaMsg = el('2fa-signin-msg');
  if (twoFaMsg) twoFaMsg.innerHTML = '';
}

export function authMsg(elId: string, type: string, msg: string): void {
  const msgEl = el(elId);
  if (msgEl) msgEl.innerHTML = '<div class="login-msg ' + type + '">' + esc(msg) + '</div>';
}

// ─── Terms & Conditions gate helpers ────────────────────────────────────────

export function hasAcceptedTerms(): boolean {
  if (!state.currentUserEmail) return false;
  return localStorage.getItem('noteflow-terms-' + getUserKey()) === 'accepted';
}

export function showTermsGate(): void {
  const loginScreen = el('login-screen');
  if (loginScreen) loginScreen.style.display = 'none';
  el('dashboard')?.classList.remove('visible');
  el('app-wrapper')?.classList.remove('visible');
  const termsGate = el('terms-gate');
  if (termsGate) termsGate.style.display = 'flex';

  const cb = el('terms-accept-checkbox') as HTMLInputElement | null;
  const btn = el('terms-accept-btn') as HTMLButtonElement | null;
  if (cb && btn) {
    cb.checked = false;
    btn.disabled = true;
    btn.style.opacity = '0.5';
    btn.style.cursor = 'not-allowed';
    cb.onchange = function () {
      btn.disabled = !cb.checked;
      btn.style.opacity = cb.checked ? '1' : '0.5';
      btn.style.cursor = cb.checked ? 'pointer' : 'not-allowed';
    };
  }
}

export function hideTermsGate(): void {
  const termsGate = el('terms-gate');
  if (termsGate) termsGate.style.display = 'none';
}

export function persistTermsAcceptance(): void {
  if (!state.currentUserEmail) return;
  localStorage.setItem('noteflow-terms-' + getUserKey(), 'accepted');
  if (FIREBASE_ENABLED) {
    const uid = getFirebaseUid();
    if (uid) {
      try {
        firebase.firestore().collection('users').doc(uid).set(
          { termsAcceptedAt: new Date().toISOString(), termsVersion: '2026-04-12' },
          { merge: true }
        );
      } catch (_e) { /* best-effort */ }
    }
  }
}

// ─── Subscription gate UI ───────────────────────────────────────────────────

export function showSubscriptionGate(): void {
  const loginScreen = el('login-screen');
  if (loginScreen) loginScreen.style.display = 'none';
  el('dashboard')?.classList.remove('visible');
  el('app-wrapper')?.classList.remove('visible');
  const subGate = el('subscription-gate');
  if (subGate) subGate.style.display = 'flex';
}

export function hideSubscriptionGate(): void {
  const subGate = el('subscription-gate');
  if (subGate) subGate.style.display = 'none';
}
